import { useState } from "react";
import { trpc } from "@/admin/lib/trpc";
import { Button } from "@nosible/shared/components/ui/button";
import { Briefcase, ChevronLeft, ChevronRight, Clock, CheckCircle, User } from "lucide-react";
import { Link } from "react-router";

export function AdminEmailJobList() {
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data, isLoading, error } = trpc.email.listEmailJobs.useQuery({
    page,
    pageSize,
  });

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-chromium-mist">Loading email jobs...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="p-4 bg-signal-blue/20 rounded-lg text-silica">
          Error loading email jobs: {error.message}
        </div>
      </div>
    );
  }

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getJobStatus = (startedAt: Date | string, completedAt: Date | string | null) => {
    if (completedAt) {
      return { label: "Completed", icon: CheckCircle, color: "text-neon-green" };
    }
    return { label: "In Progress", icon: Clock, color: "text-chromium-mist" };
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-silica font-display flex items-center gap-2">
          <Briefcase className="h-6 w-6" />
          Email Jobs
        </h1>
        <p className="text-chromium-mist mt-1">
          View all email campaigns and bulk sends.
        </p>
      </div>

      {!data || data.jobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-chromium-mist">
          <Briefcase className="h-12 w-12 mb-3 opacity-50" />
          <p>No email jobs found</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-slate-core rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-obsidian">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-silica">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-silica">
                    Subject
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-silica">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-silica">
                    Created By
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-silica">
                    Started At
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-silica">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-chromium-mist/10">
                {data.jobs.map((job) => {
                  const status = getJobStatus(job.startedAt, job.completedAt);
                  const StatusIcon = status.icon;
                  return (
                    <tr key={job.id} className="hover:bg-obsidian/50 transition-colors">
                      <td className="px-4 py-3 text-sm text-silica font-medium">
                        {job.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-chromium-mist max-w-xs truncate">
                        {job.subject}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <StatusIcon className={`h-4 w-4 ${status.color}`} />
                          <span className="text-sm text-chromium-mist">
                            {status.label}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm text-chromium-mist">
                          <User className="h-3 w-3" />
                          {job.creatorName || job.creatorEmail || "Unknown"}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-chromium-mist">
                        {formatDate(job.startedAt)}
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/emails/jobs/${job.id}`}>
                          <Button variant="outline" size="sm">
                            View Details
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {data.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-chromium-mist">
                Page {data.pagination.page} of {data.pagination.totalPages} (
                {data.pagination.total} total)
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  variant="outline"
                  size="sm"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  onClick={() => setPage((p) => Math.min(data.pagination.totalPages, p + 1))}
                  disabled={page === data.pagination.totalPages}
                  variant="outline"
                  size="sm"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
